import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import styles from './TopTenFeatures.module.css';

const TOP_TEN_FEATURES = [
  {
    rank: 1,
    title: 'Complete Data Ownership',
    description:
      'Your contacts and relationships belong to you—not a platform. MyPeopleDatabase stores your data on your own computer, so you exclusively own and control your network and everything you know about it.',
  },
  {
    rank: 2,
    title: 'Advanced Contact Manager',
    description:
      'Store all your contacts in one secure location with customizable fields and powerful search capabilities designed for the modern contact manager experience.',
  },
  {
    rank: 3,
    title: 'Smart Organization with Groups & Tags',
    description:
      'Organize contacts by groups, tags, and relationships to quickly find the right people when you need them.',
    link: { href: '/features-groups', text: 'Learn more about Groups →' },
  },
  {
    rank: 4,
    title: 'AI Features (MCP Server)',
    description:
      'Use AI tools with your people database via a local MCP (Model Context Protocol) server—search contacts, list groups, and get smart summaries while keeping control.',
    link: { href: '/features-ai', text: 'Learn more about AI features →' },
  },
  {
    rank: 5,
    title: 'Private Sharing Groups',
    description:
      'Share selected contact information privately with the people you trust—family, friends, or colleagues—without handing your data to a social platform.',
    link: { href: '/private-sharing-groups', text: 'Learn about private sharing →' },
  },
  {
    rank: 6,
    title: 'Merge Duplicate Contacts',
    description:
      'Consolidate duplicate entries into a single clean record to keep your address book accurate and uncluttered.',
    link: { href: '/consolidate', text: 'Consolidate your contacts →' },
  },
  {
    rank: 7,
    title: 'Calendar Reminders',
    description:
      'See upcoming birthdays and important anniversaries so you can stay connected with the people who matter.',
  },
  {
    rank: 8,
    title: 'Personal Knowledge Management',
    description:
      'Use MyPeopleDatabase as the people and relationship layer of your personal knowledge management (PKM) system—a structured home for everything you know about the people in your life.',
    link: { href: '/pkm', text: 'Learn more about PKM →' },
  },
  {
    rank: 9,
    title: 'Digital Address Book',
    description:
      'A complete address book solution that connects with your existing tools and services for seamless import and export capabilities.',
  },
  {
    rank: 10,
    title: 'Privacy Controls',
    description:
      'Advanced security features keep your people data safe with customizable privacy settings.',
  },
];

export default function TopTenFeatures() {
  return (
    <>
      <Helmet>
        <title>Top Ten Features | MyPeopleDatabase</title>
        <meta
          name="description"
          content="Discover the top ten features of MyPeopleDatabase: complete data ownership, smart groups, AI integration, private sharing, duplicate merging, and more."
        />
        <meta
          name="keywords"
          content="top features, contact manager, address book, MyPeopleDatabase, data ownership"
        />
      </Helmet>
      <div className={styles.container}>
        <div className={styles.overlay} />
        <div className={styles.content}>
          <h1 className={styles.title}>Top Ten Features of MyPeopleDatabase</h1>
          <p className={styles.subtitle}>
            The capabilities that make MyPeopleDatabase the contact manager for people who take
            their relationships—and their data—seriously.
          </p>

          <ol className={styles.featuresList}>
            {TOP_TEN_FEATURES.map(feature => (
              <li key={feature.rank} className={styles.feature}>
                <div className={styles.rankBadge}>{feature.rank}</div>
                <div className={styles.featureBody}>
                  <h2>{feature.title}</h2>
                  <p>{feature.description}</p>
                  {feature.link && (
                    <Link to={feature.link.href} className={styles.featureLink}>
                      {feature.link.text}
                    </Link>
                  )}
                </div>
              </li>
            ))}
          </ol>

          <div className={styles.ctaSection}>
            <h2>See the full feature list</h2>
            <p>
              These are just the highlights—MyPeopleDatabase has even more to help you manage the
              people in your life.
            </p>
            <div className={styles.ctaButtons}>
              <Link to="/features" className={styles.primaryCta}>
                Explore All Features
              </Link>
              <Link to="/buy" className={styles.secondaryCta}>
                Get MyPeopleDatabase
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
